import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchUser } from '../Utils/SupabaseServices/UserDatabase';
import { isAbortError } from '../Utils/errors/isAbortError';
interface SidebarStateContextType {
  option: string;
  isEmail: boolean | undefined;
  isName: boolean | undefined;
  isPhone: boolean | undefined;
  isPhoneEmpty: boolean;
  tab: number | undefined;
  isEmailVerified: boolean | undefined;
  handleClick: (option: string) => void;
  setOptionDirect: (value: string) => void;
  setIsEmail: (value: boolean) => void;
  setIsName: (value: boolean) => void;
  setIsPhone: (value: boolean) => void;
  setIsPhoneEmpty: (value: boolean) => void;
  setTab: (value: number | undefined) => void;
  setIsEmailVerified: (value: boolean) => void;
  loading: boolean;
  setLoading: (value: boolean) => void;
  fetchEmail: () => void;
}

const SidebarStateContext = createContext<SidebarStateContextType | undefined>(undefined);

export const SidebarStateProvider: React.FC<{ 
    children: React.ReactNode;
    username?: string;
}> = ({ children, username: usernameFromProps }) => {
    const location = useLocation();
    const { username: usernameFromParams } = useParams();
    const navigate = useNavigate();
    const username = usernameFromProps || usernameFromParams;
    const isFollowerView =
      location.pathname.includes("preview") ||
      location.pathname.includes("add-member");
    const shouldFetchAccountChecks =
      location.pathname.startsWith("/dashboard") ||
      location.pathname.startsWith("/loading") ||
      location.pathname.startsWith("/add-member");
    const [option, setOption] = useState<string>(
      isFollowerView ? "" : "Home"
    );
    const [isEmail, setIsEmail] = useState<boolean | undefined>(undefined);
    const [isName, setIsName] = useState<boolean | undefined>(undefined);
    const [isPhone, setIsPhone] = useState<boolean | undefined>(undefined);
    const [isPhoneEmpty, setIsPhoneEmpty] = useState(false);
    const [tab, setTab] = useState<number | undefined>();
    const [loading, setLoading] = useState(true);
    const [isEmailVerified, setIsEmailVerified] = useState<boolean | undefined>(
      undefined
    );
    const setOptionDirect = useCallback((value: string) => {
      setOption(value);
    }, []);
    const handleClick = (newOption: string) => {
        if (isFollowerView) {
            navigate("/dashboard", { state: { from: "follower" } });
            setOption(newOption);
        } else {
            setOption(newOption);
        }
    };

    const fetchEmail = useCallback(async () => {
        const effectiveUsername = username || localStorage.getItem("loggedIn") || undefined;
        if (!shouldFetchAccountChecks || !effectiveUsername) {
            setLoading(false);
            return;
        }
        try {
            const userData = await fetchUser(effectiveUsername);
            if (!userData) {
                setLoading(false);
                return;
            }

            const hasEmail = userData.email !== "";
            const hasName = userData.name !== "";
            const isVerified = userData.email_verify;
            setIsEmail(hasEmail);
            setIsName(hasName);
            setIsPhone(true);
            setIsEmailVerified(isVerified);

            if (!hasEmail || !hasName || !isVerified) {
                handleClick("Account");
            }

            setLoading(false);
        } catch (error: any) {
            if (isAbortError(error)) {
                setLoading(false);
                return;
            }
                console.error("Error fetching user data:", error);
            setLoading(false);
        }
    }, [username, shouldFetchAccountChecks]);

    useEffect(() => {
        fetchEmail();
    }, [fetchEmail]);

    return (
        <SidebarStateContext.Provider value={{
            option,
            isEmail,
            isName,
            isPhone,
            isPhoneEmpty,
            tab,
            isEmailVerified,
            handleClick,
            setOptionDirect,
            setIsEmail,
            setIsName,
            setIsPhone,
            setIsPhoneEmpty,
            setTab,
            setIsEmailVerified,
            loading,
            setLoading,
            fetchEmail
        }}>
            {children}
        </SidebarStateContext.Provider>
    );
};

export const useSidebarState = () => {
    const context = useContext(SidebarStateContext);
    if (context === undefined) {
        throw new Error('useSidebarState must be used within a SidebarStateProvider');
    }
    return context;
}; 